// Statistical utility functions for correlation analysis

export interface CorrelationResult {
  coefficient: number;
  strength: 'Starkt' | 'Måttligt' | 'Svagt' | 'Inget samband';
  direction: 'positivt' | 'negativt' | 'neutralt';
  color: string;
  text: string;
}

/**
 * Calculate Pearson correlation coefficient between two arrays
 */
export function calculateCorrelation(data: Array<{ x: number; y: number }>): number {
  if (data.length < 2) return NaN;
  
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  const n = data.length;

  for (const { x, y } of data) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
    sumY2 += y * y;
  }

  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

  if (denominator === 0) return 0;
  return numerator / denominator;
}

/**
 * Get correlation description with strength, direction, and styling
 */
export function getCorrelationDescription(r: number, n: number, threshold: number = 8): CorrelationResult {
  // Om underlaget är för litet, visa bara antalet punkter
  if (n < threshold) {
    return { 
      coefficient: r,
      strength: 'Inget samband',
      direction: 'neutralt',
      color: 'text-slate-500',
      text: `Visar ${n} skolor`
    };
  }

  const absR = Math.abs(r);
  
  let strength: CorrelationResult['strength'] = 'Inget samband';
  if (absR >= 0.7) strength = 'Starkt';
  else if (absR >= 0.4) strength = 'Måttligt';
  else if (absR >= 0.2) strength = 'Svagt';

  let direction: CorrelationResult['direction'] = 'neutralt';
  let color = 'text-slate-500';
  
  if (strength !== 'Inget samband') {
    direction = r > 0 ? 'positivt' : 'negativt';
    color = r > 0 ? 'text-emerald-600' : 'text-rose-600';
  }
  
  const text = strength === 'Inget samband' 
    ? `Inget samband (n=${n})` 
    : `${strength} ${direction} samband (r=${r.toFixed(2)}, n=${n})`;

  return {
    coefficient: r,
    strength,
    direction,
    color,
    text
  };
}
